import { useMemo, useState } from "react";
import { fileUrl } from "@/lib/api";
import { Instagram, ChevronLeft, ChevronRight, Heart, MessageCircle, Send, Bookmark } from "lucide-react";

/**
 * InstagramPreview — admin-only sandbox showing how a piece will read on Instagram
 * once cross-posted. Filters the item's photos to those with the "ig" channel
 * enabled (a path missing from photo_channels counts as enabled, mirroring the
 * storefront filter contract).
 *
 * Two panels side-by-side:
 *  (a) Carousel — a square post simulation with the brand handle, dot pagination,
 *      and the description as caption preview.
 *  (b) Profile grid — the standard 3-column IG profile mosaic so the admin sees
 *      where this piece will sit in their feed grid (the hero is always slotted
 *      into the centre cell since the carousel cover uses the first IG-enabled photo).
 *
 * Props:
 *  - photos: ordered string[] of paths
 *  - photoChannels: {path: string[]}
 *  - item: {title, brand, description, sku}
 *  - settings: optional, currently unused (could pull the IG handle later)
 */
export default function InstagramPreview({ photos, photoChannels, item, handle = "@maisonpilet" }) {
  const igPhotos = useMemo(() => {
    return (photos || []).filter((p) => {
      const chans = photoChannels?.[p];
      return chans === undefined || chans.includes("ig");
    });
  }, [photos, photoChannels]);

  const [slide, setSlide] = useState(0);

  if (igPhotos.length === 0) {
    return (
      <div className="border border-dashed border-taupe/60 p-6 text-center" data-testid="ig-preview-empty">
        <Instagram className="w-5 h-5 text-taupe mx-auto mb-2" />
        <p className="text-xs text-taupe italic">no photographs are toggled for instagram. flip the IG button on any photo above to populate this preview.</p>
      </div>
    );
  }

  const safeSlide = Math.min(slide, igPhotos.length - 1);
  const prev = () => setSlide(Math.max(0, safeSlide - 1));
  const next = () => setSlide(Math.min(igPhotos.length - 1, safeSlide + 1));
  const cover = igPhotos[0];

  const caption = (item?.description || `${item?.brand || ""} ${item?.title || ""}`.trim()).slice(0, 220);

  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="instagram-preview">
      {/* CAROUSEL POST simulation */}
      <div className="bg-ivory border border-taupe">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-taupe/40">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brass to-espresso flex items-center justify-center text-ivory text-[0.65rem] font-semibold">M</div>
          <div className="flex-1">
            <p className="text-sm text-charcoal lowercase tracking-wide" data-testid="ig-handle">{handle}</p>
            <p className="text-[0.6rem] text-taupe lowercase">maison · atelier</p>
          </div>
          <span className="text-taupe text-xl leading-none">…</span>
        </div>
        <div className="relative bg-charcoal/5">
          <img
            src={fileUrl(igPhotos[safeSlide])}
            alt=""
            className="w-full aspect-square object-cover"
            data-testid={`ig-carousel-slide-${safeSlide}`}
          />
          {igPhotos.length > 1 && (
            <>
              {safeSlide > 0 && (
                <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 bg-ivory/90 hover:bg-ivory rounded-full p-1.5 border border-taupe" aria-label="previous slide" data-testid="ig-prev">
                  <ChevronLeft className="w-3.5 h-3.5 text-charcoal" />
                </button>
              )}
              {safeSlide < igPhotos.length - 1 && (
                <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 bg-ivory/90 hover:bg-ivory rounded-full p-1.5 border border-taupe" aria-label="next slide" data-testid="ig-next">
                  <ChevronRight className="w-3.5 h-3.5 text-charcoal" />
                </button>
              )}
              <div className="absolute top-3 right-3 bg-charcoal/70 text-ivory text-[0.6rem] tracking-widest uppercase px-2 py-0.5 rounded-full" data-testid="ig-slide-counter">
                {safeSlide + 1} / {igPhotos.length}
              </div>
            </>
          )}
        </div>
        <div className="px-4 py-3">
          <div className="flex items-center gap-4 mb-2">
            <Heart className="w-5 h-5 text-charcoal" />
            <MessageCircle className="w-5 h-5 text-charcoal" />
            <Send className="w-5 h-5 text-charcoal" />
            <Bookmark className="w-5 h-5 text-charcoal ml-auto" />
          </div>
          {igPhotos.length > 1 && (
            <div className="flex justify-center gap-1.5 mb-2" data-testid="ig-dots">
              {igPhotos.map((path, i) => (
                <span key={path} className={`w-1.5 h-1.5 rounded-full ${i === safeSlide ? "bg-charcoal" : "bg-taupe/50"}`} />
              ))}
            </div>
          )}
          <p className="text-sm text-charcoal leading-relaxed" data-testid="ig-caption">
            <span className="font-medium">{handle}</span>{" "}
            <span className="lowercase">{caption || "no description set."}</span>
          </p>
          <p className="text-[0.65rem] tracking-widest uppercase text-taupe mt-2">ref · {item?.sku || "—"}</p>
        </div>
      </div>

      {/* PROFILE-GRID mosaic */}
      <div>
        <p className="editorial-label mb-3">where it sits in your feed grid</p>
        <div className="grid grid-cols-3 gap-0.5 border border-taupe/40" data-testid="ig-grid-mosaic">
          {Array.from({ length: 9 }).map((_, i) => {
            const isCenter = i === 4;
            return (
              <div key={`grid-cell-${i}`} className={`aspect-square ${isCenter ? "ring-2 ring-brass ring-inset" : ""} relative overflow-hidden`}>
                {isCenter ? (
                  <img src={fileUrl(cover)} alt="" className="w-full h-full object-cover" data-testid="ig-grid-self" />
                ) : (
                  <div className="w-full h-full bg-linen flex items-center justify-center">
                    <span className="text-taupe text-[0.55rem] tracking-widest uppercase opacity-40">past post</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-[0.65rem] tracking-widest uppercase text-taupe mt-2">cover · the first ig-enabled photo</p>
        <ul className="mt-4 text-xs text-espresso leading-relaxed space-y-1">
          <li>· {igPhotos.length} photo{igPhotos.length === 1 ? "" : "s"} will post as a carousel</li>
          <li>· caption preview shows the description above</li>
          <li>· reorder photos in the board above to change the carousel sequence</li>
        </ul>
      </div>
    </div>
  );
}
